var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Notification, NotificationPortal } from "@animech/ui";
import * as Styled from "./calculate-button.style";
import { useAppDispatch, useAppSelector, selectors, actions } from "store";
import { TimedNotification } from "ui/components/elements/timed-notification";
import { GoogleAnalyticsElement } from "ui/components/elements/ga-element/ga-element";
import { googleAnalyticsEvents } from "utility/constants";
var EMPTY_GRIP_POSITION_ERRORS = new Set([
    "notifications.empty_grip_positions",
    "notifications.empty_grip_positions_decentralized",
]);
export var CalculateButton = function () {
    var minAnimationDurationSeconds = 1;
    var t = useTranslation().t;
    var dispatch = useAppDispatch();
    var _a = __read(useState(null), 2), warningMessage = _a[0], setWarningMessage = _a[1];
    var _b = __read(useState(false), 2), showRemoveConfirm = _b[0], setShowRemoveConfirm = _b[1];
    var _c = __read(useState(0), 2), countdownTimer = _c[0], setCountdownTimer = _c[1];
    var _d = __read(useState(false), 2), awaitingResult = _d[0], setAwaitingResult = _d[1];
    var isSystemPerformanceLoading = useAppSelector(selectors.selectIsPerformanceLoading);
    var sanityCheckData = useAppSelector(selectors.selectSanityCheckData);
    var isPerformanceDataValid = useAppSelector(selectors.selectIsPerformanceDataValid);
    var hasPendingSanityCheck = useAppSelector(selectors.selectHasPendingSanityCheck);
    var structuralErrorMessage = useAppSelector(selectors.selectCalculatePerformanceStructuralErrorMessage);
    var emptyGripPositionIds = useAppSelector(selectors.selectEmptyGripPositionIds);
    // The button is clickable as soon as the system is structurally valid — it no
    // longer waits for the in-flight calculation. Clicking while a calculation is
    // still running puts the button into its progress state (see `onCalculate`).
    var enabled = useAppSelector(selectors.selectCalculatePerformanceEnabled);
    useEffect(function () {
        countdownTimer > 0 &&
            isSystemPerformanceLoading &&
            setTimeout(function () { return setCountdownTimer(function (current) { return Math.max(current - 1, 0); }); }, 1000);
    }, [countdownTimer, isSystemPerformanceLoading]);
    var calculationTimeSeconds = Math.max((sanityCheckData === null || sanityCheckData === void 0 ? void 0 : sanityCheckData.estimatedCalculationTime) || 0, minAnimationDurationSeconds);
    var handleRemoveConfirm = useCallback(function () {
        var e_1, _a;
        try {
            for (var emptyGripPositionIds_1 = __values(emptyGripPositionIds), emptyGripPositionIds_1_1 = emptyGripPositionIds_1.next(); !emptyGripPositionIds_1_1.done; emptyGripPositionIds_1_1 = emptyGripPositionIds_1.next()) {
                var id = emptyGripPositionIds_1_1.value;
                dispatch(actions.deleteNode(id));
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (emptyGripPositionIds_1_1 && !emptyGripPositionIds_1_1.done && (_a = emptyGripPositionIds_1.return)) _a.call(emptyGripPositionIds_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        setShowRemoveConfirm(false);
    }, [dispatch, emptyGripPositionIds]);
    var onCalculate = useCallback(function () {
        if (structuralErrorMessage) {
            if (EMPTY_GRIP_POSITION_ERRORS.has(structuralErrorMessage)) {
                setShowRemoveConfirm(true);
            }
            else {
                setWarningMessage(t(structuralErrorMessage));
            }
            return;
        }
        setCountdownTimer(calculationTimeSeconds);
        // The sanity check only comes back with the calculation result, so we may
        // be starting before it's known. Remember that the user is waiting so we
        // can surface a failed sanity check once the calculation settles.
        setAwaitingResult(true);
        dispatch(actions.showPerformanceData());
    }, [dispatch, structuralErrorMessage, calculationTimeSeconds, t]);
    // Once the calculation the user is waiting on has fully settled, surface a
    // failed sanity check (the data itself is withheld by `showPerformanceData`).
    useEffect(function () {
        if (!awaitingResult ||
            isSystemPerformanceLoading ||
            hasPendingSanityCheck) {
            return;
        }
        setAwaitingResult(false);
        if (sanityCheckData && !sanityCheckData.validationPassed) {
            setWarningMessage(t("notifications.sanity_check_failed"));
        }
        else if (sanityCheckData && !sanityCheckData.partSecuredReachable) {
            setWarningMessage(t("notifications.full_calculation_ps_not_reached"));
        }
    }, [
        awaitingResult,
        isSystemPerformanceLoading,
        hasPendingSanityCheck,
        sanityCheckData,
        t,
    ]);
    var calculationComplete = isPerformanceDataValid && enabled;
    var calculateButtonText = useMemo(function () {
        if (isSystemPerformanceLoading) {
            return t("topbar.calculating", {
                time: Math.round(countdownTimer),
            });
        }
        return calculationComplete
            ? t("topbar.calculate_button_complete")
            : t("topbar.calculate_button");
    }, [calculationComplete, isSystemPerformanceLoading, countdownTimer, t]);
    var calculateButtonColor = useMemo(function () { return (isPerformanceDataValid || !enabled ? "primary" : "tertiary"); }, [enabled, isPerformanceDataValid]);
    return (_jsxs(Styled.ButtonContainer, { children: [_jsx(GoogleAnalyticsElement, { event: calculationComplete ? null : googleAnalyticsEvents.click.runSimulation, children: _jsx(Styled.CalculationButton, { isActive: isSystemPerformanceLoading, durationMs: calculationTimeSeconds * 1000, onClick: onCalculate, disabled: !enabled, color: calculateButtonColor, children: calculateButtonText }) }), warningMessage !== null && (_jsx(TimedNotification, { text: warningMessage, timeout: 10000, color: "alert", onClose: function () { return setWarningMessage(null); } })), _jsx(NotificationPortal, { open: showRemoveConfirm, onShouldClose: function () { return setShowRemoveConfirm(false); }, children: _jsxs(Notification, { color: "alert", className: "globally-styled-notification alert", children: [t("remove_empty_grip_positions_modal.body"), _jsx(Styled.NotificationActions, { children: _jsx(Button, { type: "auto", color: "alert", onClick: handleRemoveConfirm, children: t("remove_empty_grip_positions_modal.confirm") }) })] }) })] }));
};
